import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { INSTAGRAM_USERNAME_URL } from "schemas/social";

import {
  isValidUsername,
  sanitizeInput,
} from "features/Auth/Onboarding/AboutForm/helpers";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface InstagramSetupProps {
  closeDialog?: () => void;
}

export default function InstagramSetup({ closeDialog }: InstagramSetupProps) {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { setProfile, currentOrg } = useContext(OrganizationUserContext);

  const [instagramHandle, setInstagramHandle] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  const handleInstagramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedInput = sanitizeInput(value.trim());
    setInstagramHandle(sanitizedInput);

    if (sanitizedInput && !isValidUsername(sanitizedInput)) {
      setValidationError(
        "Must contain only letters, numbers, underscores, hyphens or periods",
      );
    } else {
      setValidationError(undefined);
    }
  };

  const saveInstagramProfile = async (handle: string) => {
    if (!handle || !currentOrg?.id) return;

    setLoading(true);
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding`,
        "PUT",
        {},
        {
          instagramUrl: `${INSTAGRAM_USERNAME_URL}${handle}`,
          page: "Instagram Sidebar",
        },
      );

      setProfile((prev) => ({
        ...prev,
        ...organizationProfile,
      }));

      setAlert("Instagram account connected successfully!", "success");
    } catch (error: any) {
      setAlert(error.message || "Failed to save Instagram account", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!instagramHandle || validationError) {
      return;
    }
    saveInstagramProfile(instagramHandle);
  };

  return (
    <Box>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={styles.header}
      >
        <Grid container item xs={9} alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontSize: 14 }}>
            INSTAGRAM
          </Typography>
        </Grid>

        <Grid container item xs justifyContent="flex-end">
          <Box
            component="button"
            onClick={closeDialog}
            sx={{
              ...styles.close,
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 1,
            }}
          >
            <Box component="i" className="fa-regular fa-xmark" />
          </Box>
        </Grid>
      </Grid>

      <Box sx={[styles.content, { p: 2 }]}>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
          Now on Instagram, if you tag{" "}
          <Box
            component="a"
            href="https://instagram.com/onbento"
            target="_blank"
            rel="noopener noreferrer"
            sx={styles.link}
          >
            @onbento
          </Box>{" "}
          in a creator's post, we'll add their promoted brands here for you to
          pitch. For Virtual Assistant users, these brands are automatically
          added to next week's upcoming list. We just need your Instagram handle
          so we can connect your account.{" "}
          <a
            href="https://www.loom.com/share/ec89eca235e34f38b68db2e2de3935b3"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more here
          </a>
          .
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="handle"
              value={instagramHandle}
              onChange={handleInstagramChange}
              error={!!validationError}
              helperText={validationError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={styles.linkAdornment}>
                    {INSTAGRAM_USERNAME_URL}
                  </InputAdornment>
                ),
              }}
              sx={styles.textField}
            />
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              disabled={!instagramHandle || !!validationError || loading}
              fullWidth
              sx={styles.saveButton}
              loading={loading}
            >
              Connect Instagram
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
